import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import { SystemMutableEntity as SME } from '../../common';
import { UniqueShortId } from '../../common/id';
import { Identifier } from '../../common/identifier';
import { ImportTableDefinition } from '../../import/importTableDefinition';
import { ImportTableDefinitionRevision } from '../../import/importTableDefinitionRevision';
import { ImportTask } from '../../import/importTask';
import type { SystemCommandContext } from '../common';
import { computeCustomerMetadataHash } from './metadataHash';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(16);

export type Customer = SME & {
  projectId: Project.Id;
  id: Customer.Id;

  /** 所有する ImportTableDefinition（この定義の dataset 内で identities が一意） */
  importTableDefinitionId: ImportTableDefinition.Id;

  /** テーブル定義スコープにおける顧客識別子（importTableDefinition + identities で一意） */
  identities: Customer.Identity[];

  metadata: Customer.Metadata | undefined;

  /** 最後にこのレコードを投入/更新した ImportTableDefinitionRevision の ID */
  lastImportTableDefinitionRevisionId: ImportTableDefinitionRevision.Id | undefined;
  /** 最後にこのレコードを投入/更新した ImportTask の ID */
  lastImportTaskId: ImportTask.Id | undefined;
};

export namespace Customer {
  export type Id = Tagged<UniqueShortId, 'customerId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export const FromString = (id: string): Id => uuidGenerator.FromString(id) as Id;
  }

  export type MetadataValue = string | string[];
  export type Metadata = Record<string, MetadataValue>;

  /** merged metadata の正規化ハッシュ。詳細は ./metadataHash.ts */
  export const computeMetadataHash = computeCustomerMetadataHash;

  /**
   * テーブル定義スコープにおける顧客識別子
   * - type: 識別子タイプ（例: "customerId", "memberId"）
   * - value: 識別子の値（例: "CUST-1001"）
   */
  export type Identity = Identifier;

  export type Props = {
    id: string;
    projectId: string;
    importTableDefinitionId: string;
    identities: Identifier.Props[];
    metadata: Customer.Metadata | undefined;
    lastImportTableDefinitionRevisionId: string | undefined;
    lastImportTaskId: string | undefined;
  } & SME.Props;
  export const Construct = (props: Props): Customer => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      importTableDefinitionId: ImportTableDefinition.Id.FromString(props.importTableDefinitionId),
      identities: props.identities.map(Identifier.Construct),
      metadata: props.metadata,
      lastImportTableDefinitionRevisionId: props.lastImportTableDefinitionRevisionId
        ? ImportTableDefinitionRevision.Id.FromString(props.lastImportTableDefinitionRevisionId)
        : undefined,
      lastImportTaskId: props.lastImportTaskId ? ImportTask.Id.FromString(props.lastImportTaskId) : undefined,
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Customer.Props, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>;
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): Customer => {
    const { context, props } = params;
    const { projectId, now } = context;
    return Construct({
      id: Id.Generate(),
      projectId,
      ...props,
      ...SME.Create({ date: now }),
    });
  };

  export namespace Update {
    export type Props = Partial<
      Pick<
        //
        Customer,
        | 'identities' //
        | 'metadata'
        | 'lastImportTableDefinitionRevisionId'
        | 'lastImportTaskId'
      >
    >;
    export type Params = {
      context: SystemCommandContext;
      cur: Customer;
      props: Update.Props;
    };
  }
  export const Update = (params: Update.Params): Customer => {
    const { context, cur, props } = params;
    const { now } = context;
    return {
      ...cur,
      identities: props.identities ?? cur.identities,
      metadata: props.metadata !== undefined ? props.metadata : cur.metadata,
      lastImportTableDefinitionRevisionId:
        props.lastImportTableDefinitionRevisionId !== undefined
          ? props.lastImportTableDefinitionRevisionId
          : cur.lastImportTableDefinitionRevisionId,
      lastImportTaskId: props.lastImportTaskId !== undefined ? props.lastImportTaskId : cur.lastImportTaskId,
      ...SME.Update({ cur, date: now }),
    };
  };
}
